import { useEffect, useState, useRef } from "react";
import { ChatMessageData } from "./types";
import { saveChatHistory, getChatHistory } from "./historyDB";

interface UseChatHistoryReturn {
  messages: ChatMessageData[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageData[]>>;
}

/**
 * Hook to manage chat message history with IndexedDB persistence
 * Stores complete messages including Blobs directly in IndexedDB
 * @param historyId - Unique identifier for the field instance to ensure separate histories
 */
export const useChatHistory = (historyId?: string): UseChatHistoryReturn => {
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const isInitialMount = useRef(true);

  const historyKey = historyId || "default";

  // Load chat history from IndexedDB on mount only
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const storedMessages = await getChatHistory(historyKey);
        if (storedMessages) {
          // Recreate object URLs for stored Blobs
          const restoredMessages = storedMessages.map(msg => {
            if (msg.imageBlob) {
              return {
                ...msg,
                imageUrl: URL.createObjectURL(msg.imageBlob),
              };
            }
            return msg;
          });
          setMessages(restoredMessages);
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
      } finally {
        // Mark that initial mount and load is complete
        isInitialMount.current = false;
      }
    };

    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyKey]); // Only load on mount or when history key changes

  // Save chat history to IndexedDB when messages change
  useEffect(() => {
    // Skip save only on initial render before load completes
    if (isInitialMount.current) return;

    const saveHistory = async () => {
      try {
        // Filter out pending messages before saving
        const messagesToSave = messages.filter(msg => msg.status !== "pending");

        await saveChatHistory(historyKey, messagesToSave);
      } catch (error) {
        console.error("Error saving chat history:", error, { messageCount: messages.length });
      }
    };

    saveHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, historyKey]);

  return {
    messages,
    setMessages,
  };
};
